import { apiRequest } from "../../api/apiClient";
import type {
  Role,
  CreateRoleRequest,
  UpdateRoleRequest,
  ModuleNode,
  CountyOption,
  LinkRoleCountyRequest,
  RoleCountyLink,
} from "../../types/role.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  // For successful DELETE or status with no body, return empty object
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const rolesService = {
  /**
   * Get all roles
   */
  getRoles: async (): Promise<Role[]> => {
    const response = await apiRequest("/Roles", {
      method: "GET",
    });
    return handleResponse<Role[]>(response);
  },

  /**
   * Get a single role by ID
   */
  getRoleById: async (roleId: number): Promise<Role> => {
    const response = await apiRequest(`/Roles/${roleId}`, {
      method: "GET",
    });
    return handleResponse<Role>(response);
  },

  /**
   * Create a new role
   */
  createRole: async (data: CreateRoleRequest): Promise<Role> => {
    const response = await apiRequest("/Roles", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<Role>(response);
  },

  /**
   * Update an existing role
   */
  updateRole: async (
    roleId: number,
    data: UpdateRoleRequest,
  ): Promise<Role> => {
    const response = await apiRequest(`/Roles/${roleId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<Role>(response);
  },

  /**
   * Delete a role
   */
  deleteRole: async (roleId: number): Promise<void> => {
    const response = await apiRequest(`/Roles/${roleId}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },

  /**
   * Toggle role status (active/inactive)
   */
  toggleRoleStatus: async (
    roleId: number,
    isActive: boolean,
  ): Promise<Role> => {
    const response = await apiRequest(`/Roles/${roleId}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ isActive }),
    });
    return handleResponse<Role>(response);
  },

  /**
   * Get role permission tree (modules and their permissions)
   */
  getRolePermissionTree: async (roleId: number): Promise<ModuleNode[]> => {
    const response = await apiRequest(`/RolePermission/role/${roleId}/tree`, {
      method: "GET",
    });
    return handleResponse<ModuleNode[]>(response);
  },

  /**
   * Toggle a specific permission for a role
   */
  toggleRolePermission: async (
    roleId: number,
    moduleId: number,
    permissionId: number,
    enable: boolean,
  ): Promise<void> => {
    const response = await apiRequest("/RolePermission/toggle", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        roleId,
        moduleId,
        permissionId,
        enable,
      }),
    });
    await handleResponse<void>(response);
  },

  /**
   * Get all counties
   */
  getCounties: async (): Promise<CountyOption[]> => {
    const response = await apiRequest("/Counties", {
      method: "GET",
    });
    return handleResponse<CountyOption[]>(response);
  },

  /**
   * Link role to county
   */
  linkRoleToCounty: async (data: LinkRoleCountyRequest): Promise<void> => {
    const response = await apiRequest("/RoleCounties", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    await handleResponse<void>(response);
  },

  /**
   * Get linked role-county records
   */
  getRoleCountyLinks: async (): Promise<RoleCountyLink[]> => {
    const response = await apiRequest("/RoleCounties", {
      method: "GET",
    });
    return handleResponse<RoleCountyLink[]>(response);
  },

  /**
   * Unlink role from county
   */
  unlinkRoleFromCounty: async (
    linkId: number | undefined,
    data: LinkRoleCountyRequest,
  ): Promise<void> => {
    if (linkId) {
      let response = await apiRequest(`/RoleCounties/${linkId}`, {
        method: "DELETE",
      });

      if (response.status === 404 || response.status === 405) {
        response = await apiRequest("/RoleCounties", {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
      }

      await handleResponse<void>(response);
      return;
    }

    const response = await apiRequest("/RoleCounties", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    await handleResponse<void>(response);
  },
};
